import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { Button } from "@/components/ui";
import { Shell } from "@/features/apply/ApplyPage";
import { passwordSetupRepository } from "@/repositories/passwordSetupRepository";

import { AuthError } from "./components/AuthError";

const MIN_LENGTH = 10;

const INPUT =
  "h-touch-lg w-full rounded-control border border-line bg-surface px-3 text-sm text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

/**
 * Where the set-password email lands. How a newly approved owner first gets in.
 *
 * It signs nobody in. On success it sends the person to the ordinary sign-in
 * screen, already on the email tab, so there is one way into a session.
 */
export function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const uid = params.get("uid") ?? "";
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const save = useMutation({
    mutationFn: () =>
      passwordSetupRepository.setPassword({
        uid,
        token,
        new_password: password,
      }),
  });

  if (!uid || !token) {
    return (
      <Shell
        title="This link is incomplete"
        lead="Open the link straight from your email."
      >
        <p className="text-sm text-ink-600">
          If your email app split the link over two lines, copy the whole
          address into your browser. If it still does not work, ask SmartSpaza
          to send you a new one.
        </p>
      </Shell>
    );
  }

  if (save.isSuccess) {
    return (
      <Shell
        title="Your password is set"
        lead="You can sign in to SmartSpaza now."
      >
        <p className="mb-4 text-sm text-ink-600">
          Sign in on the <strong>Back office</strong> tab with{" "}
          <strong>{save.data.email}</strong> and the password you just chose.
        </p>
        <Button
          variant="primary"
          size="pos"
          fullWidth
          onClick={() => navigate("/login?mode=password")}
        >
          Go to sign in
        </Button>
      </Shell>
    );
  }

  const mismatch = confirm.length > 0 && confirm !== password;
  const error =
    save.error instanceof ApiError
      ? save.error.code === "INVALID_SET_PASSWORD_LINK"
        ? "This link has expired or has already been used. Ask SmartSpaza to send you a new one."
        : save.error.message
      : save.error
        ? "We could not reach SmartSpaza. Check your connection and try again."
        : null;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!mismatch && password.length >= MIN_LENGTH) save.mutate();
  };

  return (
    <Shell
      title="Choose your password"
      lead="You will use it with your email address to sign in to the back office."
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="new-password"
            className="mb-1.5 block text-sm font-medium text-ink-900"
          >
            New password
          </label>
          <input
            id="new-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={MIN_LENGTH}
            required
            className={INPUT}
          />
          <p className="mt-1.5 text-xs text-ink-500">
            At least {MIN_LENGTH} characters. A short sentence is easy to
            remember and hard to guess.
          </p>
        </div>

        <div>
          <label
            htmlFor="confirm-password"
            className="mb-1.5 block text-sm font-medium text-ink-900"
          >
            Type it again
          </label>
          <input
            id="confirm-password"
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
            className={INPUT}
          />
          {mismatch && (
            <p className="mt-1.5 text-xs text-danger-700">
              The two passwords do not match.
            </p>
          )}
        </div>

        <AuthError message={error} />

        <Button
          type="submit"
          variant="primary"
          size="pos"
          fullWidth
          loading={save.isPending}
          disabled={password.length < MIN_LENGTH || confirm !== password}
        >
          Set password
        </Button>
      </form>
    </Shell>
  );
}
